import { SceneEditor, SceneViewer } from '@zakeke/zakeke-3d-core';
import React, { useEffect, useState } from 'react';
import bundleManifest from 'src/bundles_manifest.json';
import config from 'src/shared/config';
import { DesignManager } from '../designManager';
import usePreview3DStore from '../store';
import { loadFontsForDesign } from './designFontLoader';

export const Load3DSceneEditor = ({
	children,
	damMode,
	fallback,
	isConfiguratorProduct,
	notLoadDesign = false,
	onProgress,
	compositionDocId,
	variantId
}: {
	children: React.ReactNode;
	fallback: React.ReactNode;
	damMode: boolean;
	isConfiguratorProduct?: boolean;
	notLoadDesign?: boolean;
	onProgress: (percentage: number) => void;
	/** Composition document ID to load and apply attribute selections */
	compositionDocId?: string | null;
	/** Variant/color ID to apply to the 3D model after loading */
	variantId?: number | null;
}) => {
	const [setDesignManager, zkProduct, zkScene, setSceneEditor, currentSceneEditor] = usePreview3DStore((store) => [
		store.setDesignManager,
		store.zkProduct,
		store.zkScene,
		store.setSceneEditor,
		store.sceneEditor
	]);

	const [error, setError] = useState<Error | null>(null);

	if (error) {
		throw error;
	}

	useEffect(() => {
		if (!zkProduct || !zkScene) return;

		let sceneEditor: SceneEditor;

		(async () => {
			try {
				const { createCustomizer } = await import('@zakeke/zakeke-customizer-react');
				const { Preview3DConfigurationManager, SceneEditor } = await import('@zakeke/zakeke-3d-core');

				// This will inject the customizer in the global scope as MPLaza.Customizer as it
				// will trigger the chunk to be imported. Must be improved in future.
				const testCustomizer = createCustomizer(false, false, null, false, true);

				sceneEditor = await SceneEditor.createSceneEditorAsync({
					authorizationToken: config.token!,
					editorBackgroundColor: '#FFFFFF',
					showGround: false,
					showHighlight: true,
					highlightColor: '#FF5500',
					highlightOverColor: '#FF5500',
					incrementalWorkersScriptsUrls: [config.publicPath + 'assets/bundles/' + bundleManifest['globals_3d_2']]
				});

				sceneEditor.detach();

				sceneEditor.setLoadingScreen({
					displayLoadingUI: () => null,
					hideLoadingUI: () => null,
					loadingUIBackgroundColor: '',
					loadingUIText: ''
				});

				sceneEditor.updateView();

				// These values are added on runtime by the DAM in <LoadDataFromDAM /> component
				const url = zkScene.get('url');
				const sceneConfig = zkScene.get('config');

				console.log('Loading 3D scene with URL:', url);

				if (url && url.endsWith('.glb')) {
					await sceneEditor.loadFromGlbUrl(url, sceneConfig?.toJSON());
					sceneEditor.setZkScene(zkScene);
					sceneEditor.replaceModel(zkProduct); // Necessary for take screenshot
				} else if (zkScene.get('sceneID')) {
					await sceneEditor.loadSceneFromModelAsync(zkProduct, zkScene, (e) => {
						onProgress(e.loaded);
					});
				} else {
					// Wait for ES build of Zakeke 3D
					(sceneEditor as any)._zkScene = zkScene;
					(sceneEditor as any)._zkModel = zkProduct;

					await sceneEditor.createEmptySceneAsync();
				}

				// Restore the default-visible mesh set captured by the composer's
				// saveOptimizedScene into scene.metadata.visibleMeshesID. The .babylon
				// is serialized with all meshes hidden, so without this call the
				// composer/Scene Editor would render an empty model.
				(sceneEditor as any).applyVisibleMeshesFromMetadata?.();

				sceneEditor.adjustCamera(false);
				zkScene.getOrCreateGeneralConfig().set('backgroundAlpha', 1);
				sceneEditor.refreshEnvironment();

				const sceneManager = new Preview3DConfigurationManager(zkProduct, zkScene, sceneEditor.scene!);
				const selection = Zakeke.ComposerSelection.CreateComposerSelection(zkProduct);
				selection.on('attributeSelectionChanged', async (e: any) => {
					await sceneEditor.executeActionsAsync(e.attributeID, e.selectedValue, e.actions);
				});

				// If compositionDocId is provided, load the composition and setup the selection
				if (compositionDocId) {
					try {
						const $ = (window as any).jQuery;
						if ($) {
							$.ajaxSetup({ cache: true, headers: { Authorization: 'Bearer ' + config.token } });
							(window as any).$ = $;
							(window as any).jQuery = $;
						}

						const zkComposition = new MPlaza.Composition({ docID: compositionDocId });
						await new Promise<void>((resolve, reject) => {
							zkComposition.fetch({
								success: () => resolve(),
								error: (response: unknown) => {
									console.error('Failed to fetch composition:', response);
									reject(new Error('Failed to fetch composition'));
								},
								data: {
									fromBackoffice: 'true'
								}
							});
						});
						console.log('Composition loaded:', zkComposition.get('docID'));
						selection.setupOnComposition(zkComposition);

						// Execute all attribute actions to apply the composition state to the 3D scene
						const transformationScript = selection.getAllAttributeTransformationScript();
						for (const script of transformationScript) {
							await sceneEditor.executeActionsAsync(script.attributeID, script.selectedValue, script.actions);
						}

						// If the composition has a designDocID, also load and render the design
						const compositionDesignDocId = zkComposition.get('designDocID');
						if (compositionDesignDocId) {
							try {
								const design = new MPlaza.Design({ docID: compositionDesignDocId });
								design.setModel(zkProduct);

								await new Promise<void>((resolve, reject) => {
									design.fetch({
										success: () => resolve(),
										error: (response: unknown) => {
											console.error('Failed to fetch design from composition:', response);
											reject(new Error('Failed to fetch design from composition'));
										},
										data: {
											template: false,
											frombackoffice: true,
											fromOrderDesignEditor: false
										}
									});
								});

								console.log('Design from composition loaded:', design.get('docID'));

								// Load fonts before rendering the design
								await loadFontsForDesign(design);

								setTimeout(async () => await sceneEditor.renderDesignAsync(design), 100);
							} catch (designErr) {
								console.error('Error loading design from composition:', designErr);
							}
						}
					} catch (err) {
						console.error('Error loading composition:', err);
					}
				}

				// If variantId is provided, apply the variant to the 3D model
				if (variantId && variantId != 1) {
					try {
						const color = zkProduct.get('colors').find((x) => x.get('colorID') === variantId);
						if (color) {
							const attributeSelection = sceneManager.getSelectedAttributeOption(color);
							if (attributeSelection && attributeSelection.attributeGuid && attributeSelection.optionGuid) {
								const zkAttribute = zkProduct.get('attributes').find((x) => x.get('attributeGuid') === attributeSelection.attributeGuid);
								if (zkAttribute) {
									const zkOption = zkAttribute.get('options').find((x) => x.get('optionGuid') === attributeSelection.optionGuid);
									if (zkOption) {
										const actions = zkOption.get('actions').sortBy((item) => item.get('execOrder'));
										await sceneEditor.executeActionsAsync(null, null, actions);
									}
								}
							}
						}
					} catch (err) {
						console.error('Error applying variant:', err);
					}
				}

				sceneEditor.adjustCamera(false);

				// Set the base mesh, this is for old scenes where there wasnt a base mesh
				// in caso di prodotto configuratore non ho bisogno della base mesh
				if (zkScene.get('sceneID') && !damMode && !isConfiguratorProduct) {
					sceneManager.upgradeToBaseMeshes();
				}
				// in scene editor non mi serve caricare il design
				if (notLoadDesign) {
					console.log('🎨 Prodotto composer/configuratore rilevato, impostazione colori di sfondo specifici');
					sceneEditor.setEditorBackgroundColor('#282d33');
					sceneEditor.setViewerBackgroundColor('#f2f2f2');
					sceneEditor.setViewerBackgroundAlpha(1);
				} else {
					const designManager = new DesignManager(zkProduct, sceneEditor, zkScene);
					designManager.render();

					setDesignManager(designManager);
				}
				setSceneEditor(sceneEditor, sceneManager, selection);
			} catch (e) {
				console.error(e);
				setError(new Error('Error loading 3D scene', { cause: e }));
			}
		})();

		return () => {
			sceneEditor?.dispose();
		};
	}, [zkScene]);

	if (!zkProduct || !zkScene || !currentSceneEditor) return fallback;

	return children;
};

export const Load3DSceneViewer = ({
	children,
	fallback,
	onProgress,
	compositionDocId,
	variantId,
	sceneFile,
	sceneRootUrl
}: {
	children: React.ReactNode;
	fallback: React.ReactNode;
	onProgress: (percentage: number) => void;
	/** Composition document ID to load and apply attribute selections */
	compositionDocId?: string | null;
	/** Variant/color ID to apply to the 3D model after loading */
	variantId?: number | null;
	/** Serialized Babylon scene to load instead of the product scene */
	sceneFile?: string | Blob | null;
	/** Root URL for external assets referenced by the serialized scene */
	sceneRootUrl?: string | null;
}) => {
	const [zkProduct, zkScene, setSceneViewer, currentSceneViewer] = usePreview3DStore((store) => [
		store.zkProduct,
		store.zkScene,
		store.setSceneViewer,
		store.sceneViewer
	]);

	const [error, setError] = useState<Error | null>(null);

	if (error) {
		throw error;
	}

	useEffect(() => {
		if (!zkProduct || !zkScene) return;

		let sceneViewer: SceneViewer;

		(async () => {
			try {
				const { createCustomizer } = await import('@zakeke/zakeke-customizer-react');
				const { SceneViewer } = await import('@zakeke/zakeke-3d-core');

				// This will inject the customizer in the global scope as MPLaza.Customizer as it
				// will trigger the chunk to be imported. Must be improved in future.
				const testCustomizer = createCustomizer(false, false, null, false, true);

				sceneViewer = await SceneViewer.createSceneViewerAsync({
					configPreview: false,
					showGround: false,
					isViewer: true
				});

				sceneViewer.detach();

				sceneViewer.setLoadingScreen({
					displayLoadingUI: () => null,
					hideLoadingUI: () => null,
					loadingUIBackgroundColor: '',
					loadingUIText: ''
				});

				sceneViewer.updateView();

				// These values are added on runtime by the DAM in <LoadDataFromDAM /> component
				const url = zkScene.get('url');
				const sceneConfig = zkScene.get('config');
				let serializedSceneFile: File | null = null;
				if (typeof File !== 'undefined') {
					if (typeof sceneFile === 'string') {
						serializedSceneFile = new File([sceneFile], 'scene.babylon', { type: 'application/json' });
					} else if (sceneFile instanceof File) {
						serializedSceneFile = sceneFile;
					} else if (typeof Blob !== 'undefined' && sceneFile instanceof Blob) {
						serializedSceneFile = new File([sceneFile], 'scene.babylon', { type: sceneFile.type || 'application/babylon' });
					}
				}

                // Disable auto rotation
                sceneConfig?.get('camera')?.set('autoRotate', false);

				console.log('Loading 3D scene viewer with URL:', url);

				if (serializedSceneFile) {
					sceneViewer.setZkScene(zkScene);
					sceneViewer.replaceModel(zkProduct);
					await (sceneViewer as any).loadSceneFromRemoteFilesAsync(sceneRootUrl ?? null, serializedSceneFile, (e: { loaded: number }) => {
						onProgress(e.loaded);
					});
				} else if (url && url.endsWith('.glb')) {
					await sceneViewer.loadFromGlbUrl(url, sceneConfig?.toJSON());
					sceneViewer.setZkScene(zkScene);
					sceneViewer.replaceModel(zkProduct);
				} else if (zkScene.get('sceneID')) {
					await sceneViewer.loadSceneFromModelAsync(zkProduct, zkScene, (e) => {
						onProgress(e.loaded);
					});
				} else {
					(sceneViewer as any)._zkScene = zkScene;
					(sceneViewer as any)._zkModel = zkProduct;

					await sceneViewer.createEmptySceneAsync();
				}

				sceneViewer.adjustCamera(false);
				zkScene.getOrCreateGeneralConfig().set('backgroundColor', '#FFFFFF');
				zkScene.getOrCreateGeneralConfig().set('screenshotBackgroundColor', '#FFFFFF');
				zkScene.getOrCreateGeneralConfig().set('backgroundAlpha', 1);
				sceneViewer.refreshEnvironment();

				// If compositionDocId is provided, load the composition and apply attribute selections
				if (compositionDocId) {
					try {
						const $ = (window as any).jQuery;
						if ($) {
							$.ajaxSetup({ cache: true, headers: { Authorization: 'Bearer ' + config.token } });
							(window as any).$ = $;
							(window as any).jQuery = $;
						}

						const zkComposition = new MPlaza.Composition({ docID: compositionDocId });
						await new Promise<void>((resolve, reject) => {
							zkComposition.fetch({
								success: () => resolve(),
								error: (response: unknown) => {
									console.error('Failed to fetch composition:', response);
									reject(new Error('Failed to fetch composition'));
								},
								data: {
									fromBackoffice: 'true'
								}
							});
						});
						console.log('Composition loaded:', zkComposition.get('docID'));

						const selection = Zakeke.ComposerSelection.CreateComposerSelection(zkProduct);
						selection.setupOnComposition(zkComposition);

						// Execute all attribute actions to apply the composition state to the 3D scene
						const transformationScript = selection.getAllAttributeTransformationScript();
						for (const script of transformationScript) {
							await sceneViewer.executeActionsAsync(script.attributeID, script.selectedValue, script.actions);
						}

						// If the composition has a designDocID, also load and render the design
						const compositionDesignDocId = zkComposition.get('designDocID');
						if (compositionDesignDocId) {
							try {
								const design = new MPlaza.Design({ docID: compositionDesignDocId });
								design.setModel(zkProduct);

								await new Promise<void>((resolve, reject) => {
									design.fetch({
										success: () => resolve(),
										error: (response: unknown) => {
											console.error('Failed to fetch design from composition:', response);
											reject(new Error('Failed to fetch design from composition'));
										},
										data: {
											template: false,
											frombackoffice: true,
											fromOrderDesignEditor: false
										}
									});
								});

								console.log('Design from composition loaded:', design.get('docID'));

								await loadFontsForDesign(design);

								setTimeout(async () => await sceneViewer.renderDesignAsync(design), 100);
							} catch (designErr) {
								console.error('Error loading design from composition:', designErr);
							}
						}
					} catch (err) {
						console.error('Error loading composition:', err);
					}
				}

				// If variantId is provided, apply the variant to the 3D model
				if (variantId && variantId != 1) {
					try {
						const { Preview3DConfigurationManager } = await import('@zakeke/zakeke-3d-core');
						const viewerSceneManager = new Preview3DConfigurationManager(zkProduct, zkScene, sceneViewer.scene!);
						const color = zkProduct.get('colors').find((x) => x.get('colorID') === variantId);
						if (color) {
							const attributeSelection = viewerSceneManager.getSelectedAttributeOption(color);
							if (attributeSelection && attributeSelection.attributeGuid && attributeSelection.optionGuid) {
								const zkAttribute = zkProduct.get('attributes').find((x) => x.get('attributeGuid') === attributeSelection.attributeGuid);
								if (zkAttribute) {
									const zkOption = zkAttribute.get('options').find((x) => x.get('optionGuid') === attributeSelection.optionGuid);
									if (zkOption) {
										const actions = zkOption.get('actions').sortBy((item) => item.get('execOrder'));
										await sceneViewer.executeActionsAsync(null, null, actions);
									}
								}
							}
						}
					} catch (err) {
						console.error('Error applying variant:', err);
					}
				}

				sceneViewer.adjustCamera(false);

				setSceneViewer(sceneViewer);
			} catch (e) {
				console.error(e);
				setError(new Error('Error loading 3D scene viewer', { cause: e }));
			}
		})();

		return () => {
			sceneViewer?.dispose();
		};
	}, [zkScene, sceneFile, sceneRootUrl]);

	if (!zkProduct || !zkScene || !currentSceneViewer) return fallback;

	return children;
};

export default Load3DSceneEditor;
